import * as types from '../mutations'
import AxiosInstance from '@/utils/axios.instances.js'
import AuthService from '@/services/auth'
import ContractsService from '@/services/contracts'
import Permissions from '@/const/permissions'

const state = {
  me: {},
  contract: {},
  contracts: [],
  scantrustToken: '',
  error: false,
  errorMessage: '',
  language: '',
  refreshingToken: false,
  pendingRequests: []
}

const getters = {
  language: (state) => state.language,
  contract: (state) => state.contract,
  scantrustAuthToken: (state) => state.scantrustToken,
  scantrustAuthError: (state) => state.error,
  scantrustAuthErrorMessage: (state) => state.errorMessage,
  hasPausedContract: (state) => {
    return state.contracts?.find((contract) => {
      return contract.status === 'paused'
    })
  },

  isRefreshingToken: (state) => {
    return state.refreshingToken
  },

  tokenRefreshRequests: (state) => {
    return state.pendingRequests
  },

  profile: (state) => {
    return state.me
  },

  isAdmin: (state) => {
    return state.me.is_staff
  },

  isRemyCompany: (state) => {
    return state.me.plan && state.me.plan.id.indexOf('remycointreau') >= 0
  },

  isScantrustUser: (state) => {
    if (state.me) {
      return ~state.me.email.indexOf('@scantrust.com')
    }
    return false
  },

  currentPlan: (state) => {
    if (state.me.plan && state.me.plan.id !== '@not-set') {
      return state.me.plan
    }
    if (state.me.company) {
      return { id: state.me.company.plan }
    }
  },

  isAllowed: (state) => (action) => {
    if (!state.me && state.me.permissions) return false

    const permissions = state.me.permissions

    return permissions?.includes(action) || state.me.is_staff
  },

  isAvailable: (state, getters) => (feature) => {
    // Check against new plan feature. If plan do not exist, fall back to existing permission system.
    //
    if (!state.me.plan || !state.me.company) {
      return false
    }

    if (state.me.plan && state.me.plan.id !== '@not-set') {
      // New Plan Check
      return (state.me.plan.interface && state.me.plan.interface[feature]) || getters.isAdmin
    } else {
      // Old check
      if (!Permissions[state.me.company.plan] || getters.isAdmin) {
        return true
      }
      return Permissions[state.me.company.plan].features[feature] && Permissions[state.me.company.plan].features[feature].is_available
    }
  },

  hasLimit: (state, getters) => (feature) => {
    if (!state.me.plan || !state.me.company) {
      return false
    }

    if (state.me.plan && state.me.plan.id !== '@not-set') {
      if (getters.isScantrustUser) {
        return false
      } else {
        return (state.me.plan.interface && state.me.plan.interface[feature] && typeof state.me.plan.interface[feature] === 'number')
      }
    } else {
      // Old check
      if (!Permissions[state.me.company.plan] || getters.isScantrustUser) {
        return true
      }

      return Permissions[state.me.company.plan].features[feature] && Permissions[state.me.company.plan].features[feature].has_limit
    }
  },

  getLimit: (state, getters) => (feature) => {
    if (!state.me.plan || !state.me.company) {
      return 0
    }

    if (state.me.plan && state.me.plan.id !== '@not-set') {
      if (getters.isScantrustUser) {
        return true
      } else if (state.me.plan.interface && state.me.plan.interface[feature] && typeof state.me.plan.interface[feature] === 'number') {
        return state.me.plan.interface[feature]
      }
    } else {
      // Old check
      if (!Permissions[state.me.company.plan] || getters.isScantrustUser) {
        return 999
      }

      return Permissions[state.me.company.plan].features[feature] && Permissions[state.me.company.plan].features[feature].limit
    }
  }
}

const actions = {

  async loadUserContracts ({ commit }) {
    const contracts = await ContractsService.loadContracts()
    commit(types.SET_USER_CONTRACTS, contracts.results)
  },

  async loadUserContract ({ commit }, { id }) {
    const contract = await ContractsService.loadContract(id)
    commit(types.SET_ACTIVE_CONTRACT, contract)
  },

  setScantrustAuthToken ({ commit }, token) {
    AxiosInstance.setSTAuthToken(token)
    commit(types.SCANTRUST_AUTH_SUCCESS, token)
  },

  async loadUserProfile ({ commit }) {
    const profile = await AuthService.loadProfile()
    commit(types.SET_USER_PROFILE, profile)
    commit(types.CHANGE_LANGUAGE, profile.language)
    return profile
  },

  scantrustAuthError ({ commit }, err) {
    commit(types.SCANTRUST_AUTH_ERROR, err)
  },

  async updateLanguage ({ commit }, lang) {
    await AuthService.updateLanguage(lang)
    commit(types.CHANGE_LANGUAGE, lang)
  },

  async changePassword ({ commit, state }, obj) {
    try {
      await AuthService.changePassword(obj)
      state.error = false
    } catch (error) {
      commit(types.SCANTRUST_AUTH_ERROR, error)
    }
  },

  async updateCompanyInfo ({ commit }, company) {
    await AuthService.updateCompanyInfo(company)
  },

  async updateUserProfile ({ commit }, user) {
    await AuthService.updateUserProfile(user)
  },

  async linkGoogleAccount ({ dispatch }, idToken) {
    const result = await AuthService.linkGoogleAccount(idToken)
    await dispatch('loadUserProfile')
    return result
  },

  async unlinkGoogleAccount ({ dispatch }) {
    const result = await AuthService.unlinkGoogleAccount()
    await dispatch('loadUserProfile')
    return result
  },

  setIsRefreshingToken ({ commit }, err) {
    commit(types.SET_IS_REFRESHING_TOKEN, err)
  },

  resetPendingRequest ({ commit }, err) {
    commit(types.RESET_PENDING_REQUEST, err)
  },

  addPendingRequest ({ commit }, err) {
    commit(types.ADD_PENDING_REQUEST, err)
  },

  processPendingRequest ({ commit }, err) {
    commit(types.PROCESS_PENDING_REQUEST, err)
  }
}

const mutations = {
  [types.CHANGE_LANGUAGE] (state, language) {
    state.language = language
  },

  [types.SET_ACTIVE_CONTRACT] (state, contract) {
    state.contract = contract
  },

  [types.SCANTRUST_AUTH_SUCCESS] (state, token) {
    state.scantrustToken = token
  },

  [types.SCANTRUST_AUTH_ERROR] (state, error) {
    if (error) {
      state.error = true
      if (error.status === 401) {
        if (state.scantrustToken) {
          state.errorMessage = 'Invalid Token, please refresh your browser'
        } else {
          state.errorMessage = ' Authentication Token not provided'
        }
      } else {
        state.errorMessage = error.response.data
      }
    }
  },

  [types.SET_USER_PROFILE] (state, profile) {
    state.me = profile
  },

  [types.SET_USER_CONTRACTS] (state, contracts) {
    state.contracts = contracts
  },

  [types.SET_IS_REFRESHING_TOKEN] (state, isRefreshing) {
    state.refreshingToken = isRefreshing
  },

  [types.ADD_PENDING_REQUEST] (state, request) {
    state.pendingRequests = [...state.pendingRequests, request]
  },

  [types.RESET_PENDING_REQUEST] (state) {
    state.pendingRequests = []
  },

  [types.PROCESS_PENDING_REQUEST] (state, token) {
    state.pendingRequests.forEach((request) => {
      request.resolve(token)
    })
    state.pendingRequests = []
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
